import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Newspaper, TrendingUp, TrendingDown, Minus, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface NewsItem {
  title: string;
  summary: string;
  coin: "BTC" | "BNB";
  sentiment: "bullish" | "bearish" | "neutral";
  time: string;
}

const CryptoNewsSection = () => {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchNews = async () => {
    setLoading(true);
    setError(null);
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data, error: fnError } = await supabase.functions.invoke("crypto-news");
      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);
      setNews(data?.news || []);
    } catch (e: any) {
      console.error("Failed to fetch news:", e);
      setError("Unable to load news.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchNews(); }, []);

  const sentimentIcon = (s: string) => {
    if (s === "bullish") return <TrendingUp className="h-4 w-4 text-green-400" />;
    if (s === "bearish") return <TrendingDown className="h-4 w-4 text-destructive" />;
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  };

  const sentimentColor = (s: string) => {
    if (s === "bullish") return "text-green-400";
    if (s === "bearish") return "text-destructive";
    return "text-muted-foreground";
  };

  return (
    <section className="py-16">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h2 className="font-display text-3xl md:text-4xl font-bold mb-3">
            <Newspaper className="inline h-8 w-8 text-gold mr-2 -mt-1" />
            Live Crypto News
          </h2>
          <p className="text-muted-foreground font-body max-w-lg mx-auto">
            Real-time market insights on Bitcoin & BNB, updated throughout the day.
          </p>
        </motion.div>

        <div className="max-w-3xl mx-auto">
          <div className="flex justify-end mb-4">
            <Button variant="ghost" size="sm" onClick={fetchNews} disabled={loading} className="text-xs gap-1.5">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>

          {loading && news.length === 0 && (
            <div className="grid gap-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse rounded-xl border border-border bg-card/50 p-5 space-y-3">
                  <div className="h-4 w-3/4 rounded bg-muted" />
                  <div className="h-3 w-full rounded bg-muted" />
                </div>
              ))}
            </div>
          )}

          {error && <p className="text-sm text-destructive text-center py-4">{error}</p>}

          <AnimatePresence>
            <div className="grid gap-4">
              {news.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }}
                  className="rounded-xl border border-border bg-card/60 backdrop-blur-sm p-5 space-y-2 hover:border-gold-dark/30 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                      <span className={`text-[11px] font-display font-bold px-2 py-0.5 rounded ${item.coin === "BTC" ? "bg-gold/10 text-gold" : "bg-yellow-500/10 text-yellow-400"}`}>
                        {item.coin}
                      </span>
                      <h3 className="text-base font-display font-semibold leading-tight">{item.title}</h3>
                    </div>
                    {sentimentIcon(item.sentiment)}
                  </div>
                  <p className="text-sm text-muted-foreground font-body leading-relaxed">{item.summary}</p>
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-xs text-muted-foreground font-body">{item.time}</span>
                    <span className={`text-xs font-body font-medium capitalize ${sentimentColor(item.sentiment)}`}>{item.sentiment}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default CryptoNewsSection;
